import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const ServicePage = () => {
  const kidsAnimation = useScrollAnimation();
  const danceAnimation = useScrollAnimation();
  const adultAnimation = useScrollAnimation();
  const onlineAnimation = useScrollAnimation();
  const pricingAnimation = useScrollAnimation();

  useEffect(() => {
    const siteUrl = typeof VITE_SITE_URL !== 'undefined' ? VITE_SITE_URL : 'https://example.com';
    
    document.title = '岡山市英会話教室のサービス内容｜キッズから大人まで対応｜おかやま英会話教室株式会社';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市の英会話教室のサービス内容をご紹介。キッズ英会話、ダンス×英語コース、大人の英会話、オンラインレッスンまで幅広く対応。無料体験レッスン受付中。');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '岡山市英会話サービス,キッズ英会話岡山,ダンス英語教室,大人英会話レッスン,オンライン英語授業');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/service`);
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/service`);
    }
    
    // Add Schema.org JSON-LD for Service page
    const existingSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
    if (existingSchema) {
      existingSchema.remove();
    }
    
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "Service",
          "name": "キッズ英会話コース",
          "description": "3歳〜12歳まで年齢別クラス。フォニックス学習重視で、楽しみながら英語を身につけるコースです。",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "おかやま英会話教室株式会社",
            "url": `${siteUrl}/`
          },
          "areaServed": {
            "@type": "City",
            "name": "岡山市"
          },
          "offers": {
            "@type": "Offer",
            "price": "8800",
            "priceCurrency": "JPY",
            "description": "月4回レッスン（週1回）、1レッスン50分"
          }
        },
        {
          "@type": "Service",
          "name": "ダンス×英語コース",
          "description": "音楽とダンスを通じて自然に英語のリズムや表現を身につける革新的なプログラムです。",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "おかやま英会話教室株式会社",
            "url": `${siteUrl}/`
          },
          "areaServed": {
            "@type": "City",
            "name": "岡山市"
          }
        },
        {
          "@type": "Service",
          "name": "大人の英会話コース",
          "description": "ビジネス英語から日常会話まで、大人の学習者の多様なニーズに対応したコースです。",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "おかやま英会話教室株式会社",
            "url": `${siteUrl}/`
          },
          "areaServed": {
            "@type": "City",
            "name": "岡山市"
          },
          "offers": {
            "@type": "Offer",
            "price": "12000",
            "priceCurrency": "JPY",
            "description": "月4回レッスン（週1回）、1レッスン60分"
          }
        },
        {
          "@type": "Service",
          "name": "オンラインレッスン",
          "description": "場所を選ばず、質の高いレッスンを受講できるオンラインコース。完全マンツーマン指導。",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "おかやま英会話教室株式会社",
            "url": `${siteUrl}/`
          },
          "offers": {
            "@type": "Offer",
            "price": "9800",
            "priceCurrency": "JPY",
            "description": "月4回レッスン（週1回）、1レッスン45分"
          }
        }
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-schema', 'true');
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
  }, []);

  const pricingPlans = [
    {
      name: '小学生英会話',
      price: '8,800',
      features: [
        '月4回レッスン（週1回）',
        '1レッスン50分',
        '最大4名の少人数制',
        '教材費込み',
        '振替レッスン対応',
        '定期テスト対策サポート'
      ]
    },
    {
      name: '中高生コース',
      price: '8,800',
      features: [
        '月4回レッスン（週1回）',
        '1レッスン50分',
        '最大4名の少人数制',
        '教材費込み',
        '振替レッスン対応',
        '定期テスト対策サポート'
      ]
    },
    {
      name: '大人コース',
      price: '12,000',
      features: [
        '月4回レッスン（週1回）',
        '1レッスン60分',
        '最大6名の少人数制',
        '教材費別途',
        '振替レッスン対応',
        '英検対策サポート'
      ],
      recommended: true
    },
    {
      name: 'オンラインコース',
      price: '9,800',
      features: [
        '月4回レッスン（週1回）',
        '1レッスン45分',
        'マンツーマン指導',
         'デジタル教材提供',
        '24時間予約可能',
        '録画復習サポート'
      ]
    }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center mt-20">
        <img
          src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8691fc9fef1272e5a23b582a9f01cf22.jpeg"
          alt="サービス内容"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        
        <div className="relative z-10 text-center text-navy-900 max-w-4xl mx-auto px-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Service
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed">
            一人ひとりの目標に合わせた多彩なコース
          </p>
        </div>
      </section>

      {/* Junior & Senior High School English Course */}
      <section className="py-24 bg-white">
        <div 
          ref={kidsAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${kidsAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/41bb03d4ffe1887e7d803a788720f973.jpeg"
                alt="中高生英会話コース"
                className="w-full h-96 object-cover object-center rounded-2xl shadow-lg"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                JUNIOR & SENIOR HIGH
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-6">
                中高生英会話コース
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                受験英語から実践的なコミュニケーション英語まで、中高生の多様な学習ニーズに対応したコースです。
                学校での英語学習をサポートしながら、使える英語力を身につけます。
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                定期テスト対策、英検・GTEC対策、大学受験対策に加え、
                将来に役立つ実践的な英会話力の向上を目指します。同世代の仲間と一緒に学ぶことで、モチベーションを維持しながら継続できます。
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">中学生・高校生別クラス</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">定期テスト・受験対策</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">英検・GTEC対策完備</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">実践的な会話練習</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="inline-block bg-coral-500 hover:bg-coral-600 text-white px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                受講する
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dance & English Course */}
      <section className="py-24 bg-beige-50">
        <div 
          ref={danceAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${danceAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                ELEMENTARY
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-6">
                小学生英会話コース
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                小学1年生〜6年生を対象とした、楽しみながら英語の基礎力を身につけるコースです。
                フォニックス学習を中心に、聞く・話す・読む・書くの4技能をバランスよく伸ばします。
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                少人数制のクラスで一人ひとりに目が行き届く環境を整えています。
                ゲームや歌、絵本などを活用した楽しいレッスンで、英語が大好きになるお子様が続出しています。
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-coral-500 text-xl mr-3"></i>
                  <span className="text-navy-900">学年別クラス編成</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-coral-500 text-xl mr-3"></i>
                  <span className="text-navy-900">フォニックス学習重視</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-coral-500 text-xl mr-3"></i>
                  <span className="text-navy-900">英検Jr.対策</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-coral-500 text-xl mr-3"></i>
                  <span className="text-navy-900">最大6名の少人数制</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="inline-block bg-mint-500 hover:bg-mint-600 text-white px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                受講する
              </Link>
            </div>
            
            <div>
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/22165b333b1ada5c04e57efcd287ce17.jpeg"
                alt="小学生英会話コース"
                className="w-full h-96 object-cover object-center rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Adult English Course */}
      <section className="py-24 bg-white">
        <div 
          ref={adultAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${adultAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://public.readdy.ai/ai/img_res/1312720259f6b1309b0013280b3314c2.jpg"
                alt="大人の英会話コース"
                className="w-full h-96 object-cover object-center rounded-2xl shadow-lg"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="bg-navy-900 text-white px-4 py-2 rounded-full text-sm font-medium">
                ADULT
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-6">
                大人の英会話コース
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                ビジネス英語から日常会話まで、大人の学習者の多様なニーズに対応したコースです。
                実践的な場面を想定したロールプレイや、時事問題を題材にしたディスカッションを通じて、
                実用的な英語力を身につけます。
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                忙しい社会人の方も続けやすいよう、平日夜間や土曜日のクラスもご用意。
                TOEICや英検などの資格取得サポートも充実しています。
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-navy-900 text-xl mr-3"></i>
                  <span className="text-navy-900">レベル別クラス編成</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-navy-900 text-xl mr-3"></i>
                  <span className="text-navy-900">ビジネス英語対応</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-navy-900 text-xl mr-3"></i>
                  <span className="text-navy-900">資格取得サポート</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-navy-900 text-xl mr-3"></i>
                  <span className="text-navy-900">フレキシブルな時間設定</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="inline-block bg-navy-900 hover:bg-navy-900/90 text-white px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                受講する
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Online Lessons Course */}
      <section className="py-24 bg-beige-50">
        <div 
          ref={onlineAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${onlineAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="bg-mint-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                ONLINE
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-navy-900 mt-6 mb-6">
                オンラインレッスン
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                場所を選ばず、質の高いレッスンを受講できるオンラインコースです。
                最新の教育技術を活用し、対面レッスンと同等の学習効果を実現しています。
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                マンツーマンレッスンで一人ひとりのペースに合わせた指導が可能。
                レッスンの録画機能もあり、復習にもお役立ていただけます。
              </p>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">完全マンツーマン指導</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">24時間予約システム</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">レッスン録画・復習機能</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-mint-500 text-xl mr-3"></i>
                  <span className="text-navy-900">デジタル教材完備</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="inline-block bg-mint-500 hover:bg-mint-600 text-white px-8 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                受講する
              </Link>
            </div>
            
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Young%20Japanese%20woman%20in%20comfortable%20home%20office%20setting%20wearing%20headset%20having%20online%20English%20lesson%20on%20laptop%20computer%20with%20modern%20desk%20setup%20and%20good%20lighting%20in%20background&width=800&height=600&seq=online1&orientation=landscape"
                alt="オンラインレッスン"
                className="w-full h-96 object-cover object-center rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div 
          ref={pricingAnimation.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 fade-in-up ${pricingAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="text-center mb-16">
            <span className="bg-coral-500 text-white px-4 py-2 rounded-full text-sm font-medium">
              PRICING
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mt-6 mb-4">
              料金プラン
            </h2>
            <p className="text-gray-600 text-lg mt-4">
              明確でわかりやすい料金体系
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 小学生英会話 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-mint-500 transition-all duration-300">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-mint-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-emotion-happy-line text-3xl text-mint-500"></i>
                </div>
                <h3 className="font-bold text-2xl text-navy-900 mb-2">小学生英会話</h3>
                <p className="text-gray-600 text-sm">楽しく基礎力アップ</p>
              </div>
              
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <div className="text-4xl font-bold text-navy-900 mb-2">
                  ¥8,800
                  <span className="text-lg font-normal text-gray-600">/月</span>
                </div>
                <p className="text-sm text-gray-600">週1回 50分レッスン</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">フォニックス学習</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">英検Jr.対策</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">少人数制クラス</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">教材費込み</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="block w-full bg-mint-500 hover:bg-mint-600 text-white text-center py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>

            {/* 中高生英会話 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-mint-500 transition-all duration-300">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-mint-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-graduation-cap-line text-3xl text-mint-500"></i>
                </div>
                <h3 className="font-bold text-2xl text-navy-900 mb-2">中高生英会話</h3>
                <p className="text-gray-600 text-sm">定期テスト・受験対策</p>
              </div>
              
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <div className="text-4xl font-bold text-navy-900 mb-2">
                  ¥12,000
                  <span className="text-lg font-normal text-gray-600">/月</span>
                </div>
                <p className="text-sm text-gray-600">週1回 60分レッスン</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">定期テスト対策</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">英検・GTEC対策</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">受験英語サポート</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">実践会話練習</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="block w-full bg-mint-500 hover:bg-mint-600 text-white text-center py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>

            {/* 大人の英会話 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-mint-500 transition-all duration-300 relative">
              <div className="absolute top-4 right-4 bg-coral-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                人気No.1
              </div>
              
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-mint-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-line text-3xl text-mint-500"></i>
                </div>
                <h3 className="font-bold text-2xl text-navy-900 mb-2">大人の英会話</h3>
                <p className="text-gray-600 text-sm">ビジネス・日常会話</p>
              </div>
              
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <div className="text-4xl font-bold text-navy-900 mb-2">
                  ¥15,000
                  <span className="text-lg font-normal text-gray-600">/月</span>
                </div>
                <p className="text-sm text-gray-600">週1回 60分レッスン</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">ビジネス英語</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">日常会話</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">TOEIC対策</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">フリートーク</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="block w-full bg-mint-500 hover:bg-mint-600 text-white text-center py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>

            {/* オンラインレッスン */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-mint-500 transition-all duration-300">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-mint-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-computer-line text-3xl text-mint-500"></i>
                </div>
                <h3 className="font-bold text-2xl text-navy-900 mb-2">オンラインレッスン</h3>
                <p className="text-gray-600 text-sm">自宅で気軽に</p>
              </div>
              
              <div className="text-center mb-6 pb-6 border-b border-gray-200">
                <div className="text-4xl font-bold text-navy-900 mb-2">
                  ¥10,000
                  <span className="text-lg font-normal text-gray-600">/月</span>
                </div>
                <p className="text-sm text-gray-600">週1回 45分レッスン</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">Zoom使用</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">柔軟なスケジュール</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">録画復習可能</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-mint-500 text-xl mr-3 mt-0.5"></i>
                  <span className="text-gray-700">教材PDF配布</span>
                </li>
              </ul>
              
              <Link
                to="/contact"
                className="block w-full bg-mint-500 hover:bg-mint-600 text-white text-center py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600 text-sm">
              ※入会金：10,000円（初回のみ）/ 教材費：別途実費
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ServicePage;